import { NavLink } from "react-router-dom";

import { NavigationItemInterface } from "../../../types/Navigation";

import {
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import useCustomerDetails from "../../../data/useCustomerDetails";
import classNames from './../../../utils/classNames';
import { useAuth } from "../../../contexts/AuthContext";

// Using an inline props type to avoid cross-module name collisions

function SidebarTooltip({ anchorRef, label, visible, scrollContainerRef }: { anchorRef: React.RefObject<HTMLElement>; label: string; visible: boolean; scrollContainerRef: React.RefObject<HTMLElement>; }) {
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.top + rect.height / 2, left: rect.right + 8 });
  }, [anchorRef]);

  useEffect(() => {
    if (!visible) return;
    updatePosition();
    const handle = () => updatePosition();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    const sc = scrollContainerRef.current;
    sc?.addEventListener("scroll", handle, true);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
      sc?.removeEventListener("scroll", handle, true);
    };
  }, [visible, updatePosition, scrollContainerRef]);

  if (!visible) return null;
  return createPortal(
    <div
      className="pointer-events-none fixed z-[1000] rounded bg-primary-700 text-primary-50 px-2 py-1 text-xs whitespace-nowrap"
      style={{ top: pos.top, left: pos.left, transform: "translateY(-50%)" }}
    >
      {label}
    </div>,
    document.body
  );
}

function SidebarItem({ item, collapsed, scrollContainerRef }: { item: NavigationItemInterface; collapsed: boolean; scrollContainerRef: React.RefObject<HTMLElement>; }) {
  const { claims } = useAuth();
  const [hover, setHover] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);

  if(item.to == "/" && !claims?.isAdmin) return <></>

  if (item.disabled) {
    return (
      <div
        className={`group relative flex items-center ${collapsed ? "justify-center" : ""} px-2 py-2 text-sm text-gray-400 font-medium rounded-md select-none`}
        key={item.name}
        // title={item.name}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className="relative" ref={iconRef}>
          <item.icon
            className={`${collapsed ? "" : "mr-3"} h-6 w-6 flex-shrink-0 text-currentColor`}
            aria-hidden="true"
          />
        </div>
        {!collapsed && item.name}
        <SidebarTooltip anchorRef={iconRef} label={item.name} visible={collapsed && hover} scrollContainerRef={scrollContainerRef} />
      </div>
    );
  }

  if (item.external) {
    return (
      <a
        key={item.name}
        href={item.to}
        target="_blank"
        rel="noreferrer"
        className={`group relative flex items-center ${collapsed ? "justify-center" : ""} px-2 py-2 text-sm font-medium rounded-md text-primary-200`}
        // title={item.name}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className="relative" ref={iconRef}>
          <item.icon
            className={`${collapsed ? "" : "mr-3"} h-6 w-6 flex-shrink-0 text-currentColor`}
            aria-hidden="true"
          />
        </div>
        {!collapsed && item.name}
        <SidebarTooltip anchorRef={iconRef} label={item.name} visible={collapsed && hover} scrollContainerRef={scrollContainerRef} />
      </a>
    );
  }

  return (
    <NavLink
      key={item.name}
      to={item.to}
      className={({ isActive }) =>
        classNames(
          `group relative flex ${collapsed ? "justify-center" : "justify-between"} items-center px-2 py-2 text-sm font-medium rounded-md`,
          isActive ? "bg-primary-700 text-primary-50" : "text-primary-200"
        )
      }
      // title={item.name}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="flex items-center relative" ref={iconRef}>
        <item.icon
          className={`${collapsed ? "" : "mr-3"} h-6 w-6 flex-shrink-0 text-currentColor`}
          aria-hidden="true"
        />
        {!collapsed && item.name}
      </div>
      {!collapsed && item.notificationCount && item.notificationCount > 0 && (
        <span className="ml-auto inline-block py-0.5 px-3 text-xs font-medium bg-red-500 text-white rounded-full">
          {item.notificationCount}
        </span>
      )}
      {collapsed && item.notificationCount && item.notificationCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 text-[10px] font-semibold bg-red-500 text-white rounded-full">
          {item.notificationCount}
        </span>
      )}
      <SidebarTooltip anchorRef={iconRef} label={item.name} visible={collapsed && hover} scrollContainerRef={scrollContainerRef} />
    </NavLink>
  );
}

export default function DesktopSidebar({ navigation, collapsed, onToggleCollapsed }: { navigation: NavigationItemInterface[]; collapsed?: boolean; onToggleCollapsed?: () => void; }) {
  const [internalCollapsed, setInternalCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("sidebarCollapsed") === "true";
    } catch {
      return false;
    }
  });
  const isCollapsed = (collapsed ?? internalCollapsed);

  // Get unique groups, filtering out any falsy values
  const groups = useMemo((): string[] => {
    return Array.from(new Set(navigation.map((item) => item.group))).filter((group): group is string => Boolean(group));
  }, [navigation]);

  const getInitialGroupToggleState = useCallback((groups: string[], navigation: NavigationItemInterface[]) => {
    return groups.map((group) => {
      const groupItems = navigation.filter((item) => item.group === group);
      // If every item in this group is disabled, start the toggle as false
      return groupItems.every((item) => item.disabled) ? false : true;
    });
  }, []);

  // Initialize toggle states for each group based on the number of groups
  const [groupToggle, setGroupToggle] = useState(() => getInitialGroupToggleState(groups, navigation));

  // If the groups change, reset the intial state
  useEffect(() => {
    setGroupToggle(getInitialGroupToggleState(groups, navigation));
  }, [navigation]);

  // Toggle the state for a specific group
  const toggleGroup = (group: string) => {
    const groupIndex = groups.indexOf(group);
    setGroupToggle(prev =>
      prev.map((open, idx) => (idx === groupIndex ? !open : open))
    );
  };
  const renderedGroups = new Set<string>();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (onToggleCollapsed) {
      onToggleCollapsed();
    } else {
      setInternalCollapsed((prev) => {
        const next = !prev;
        try {
          localStorage.setItem("sidebarCollapsed", next.toString());
        } catch { }
        // Notify other components in the same tab
        try {
          window.dispatchEvent(new CustomEvent("sidebar:collapsed-changed", { detail: next }));
        } catch { }
        return next;
      });
    }
  };

  return (
    <div className={`hidden md:!fixed md:!inset-y-0 md:!flex md:!flex-col transition-all z-40 overflow-visible isolate mt-16 ${isCollapsed ? "md:!w-16" : "md:!w-60"}`}>
      <div className="flex flex-grow flex-col overflow-y-auto overflow-x-visible bg-primary-600" ref={scrollContainerRef}>
        
        <div className="flex flex-col my-4">
          {navigation.map((item, index) => {
            if (item.group) {
              // Only render the group header the first time we encounter this group
              if (!renderedGroups.has(item.group)) {
                renderedGroups.add(item.group);
                const groupItems = navigation.filter(nav => nav.group === item.group);
                const groupIndex = groups.indexOf(item.group);
                return (
                  <nav key={item.group} className="space-y-1 px-2 pb-4">
                    {!isCollapsed && (
                      <div
                        className="flex flex-row items-center px-2 text-md font-medium rounded-md text-currentColor cursor-pointer text-primary-200"
                        onClick={() => toggleGroup(item.group!)}
                      >
                        <span>{item.group}</span>
                        <span
                          className="w-5 h-5 ml-1 transition duration-500"
                          style={{
                            transform: groupToggle[groupIndex]
                              ? "rotate(270deg)"
                              : "rotate(90deg)",
                          }}
                        >
                          <ChevronLeftIcon />
                        </span>
                      </div>
                    )}
                    {(isCollapsed || groupToggle[groupIndex]) &&
                      groupItems.map((navItem) => (
                        <div key={navItem.to}>
                          <SidebarItem item={navItem} collapsed={isCollapsed} scrollContainerRef={scrollContainerRef} />
                        </div>
                      ))}
                  </nav>
                );
              }
              // Skip rendering duplicate group items
              return null;
            } else {
              // Render navigation items without a group directly in order
              return (
                <nav key={item.to} className="space-y-1 px-2 pb-4">
                  <SidebarItem item={item} collapsed={isCollapsed} scrollContainerRef={scrollContainerRef} />
                </nav>
              );
            }
          })}
        </div>
      </div>
    </div>
  );
}